import { Component, prop } from "@kluntje/core";
import {
  CategoryInformation,
  LocationFilterApiResponse,
  MetaInformation,
  ResultInformation,
  TerminalInformation,
} from "Interfaces/locationFilter";
import { ACTIVE, HIDDEN, SELECTED } from "Helper/cssClasses";
import { addClass, findAll, getParent, removeClass, toggleClass } from "@kluntje/js-utils/lib/dom-helpers";
import { getCurrentMQ } from "Helper/domHelper";
import {
  locationFilterResultsTemplate,
  locationFilterHallsTemplate,
  locationFilterCategoriesTemplate,
  locationFilterAlphabetTemplate,
} from "Components/fra-m-location-filter/fra-m-location-filter.template"; // eslint-disable-line
import { render } from "lit-html";
import * as dateUtils from "Components/fra-e-datetime-picker/dateUtils";
import { fetchJSON } from "Helper/apiHelper";
import { ViewportObserver } from "@kluntje/services";

interface FRALocationFilterState {
  selectedTerminal: string; // "1" | "2"
  selectedHall: string; // "A" | "B" ...
  selectedArea: "" | "transit" | "public";
  selectedCategory: string; // e.g. "fashion"
}

/**
 * Custom Element to filter locations
 */
export class FRALocationFilter extends Component {
  viewportObserver = ViewportObserver.getInstance();
  static readonly AUTO_SCROLL = "fra-auto-scroll";

  static getInitialStateFromPageUrl(): FRALocationFilterState {
    const initialStates: FRALocationFilterState = {
      selectedTerminal: "",
      selectedHall: "",
      selectedArea: "",
      selectedCategory: "",
    };

    // filter options are passed via suffix e.g. ...suffix.html/area=before/hall=A,B,C/terminal=1.html
    const params = /\.html\/(?<params>.*)\.html/.exec(location.href)?.groups!.params;
    if (params) {
      params
        .split("/")
        .map(param => param.split("="))
        .forEach(([key, value]) => {
          switch (key) {
            case "terminal":
              initialStates.selectedTerminal = value;
              break;
            case "hall":
              // in case of e.g. "A,B,C", no need to select ALL halls from the terminal. Terminal selection is enough.
              if (value.length === 1) initialStates.selectedHall = value;
              break;
            case "area":
              // before->public, after->transit
              initialStates.selectedArea = value === "before" ? "public" : "transit";
              break;
            default:
              break;
          }
        });
    }

    return initialStates;
  }

  private _halls: string[] = [];
  private _terminals: TerminalInformation[] = [];
  private _metaInformation!: MetaInformation;
  private _categories: CategoryInformation[] = [];
  private _locations: ResultInformation[] = [];
  // component is in viewport
  private _inViewport = false;

  // ajax url for all locations, current date will be added as suffix
  @prop({ required: true })
  baseUrl = "";

  constructor() {
    super({
      ui: {
        filterContainer: ".fra-m-location-filter__filter-wrapper :-one",
        terminalOptions: ".fra-m-location-filter__filter--terminal",
        areaOptions: ".fra-m-location-filter__filter--area",
        hallsContainer: ".fra-m-location-filter__halls :-one",
        categoryFilterContainer: ".fra-m-location-filter__categories :-one",
        showCategoriesBtn: ".fra-m-location-filter__show-categories-btn",
        hideCategoriesBtn: ".fra-m-location-filter__hide-categories-btn",
        alphabetContainer: ".fra-m-location-filter__alphabet-wrapper :-one",
        resultContainer: ".fra-m-location-filter__results-wrapper :-one",
      },
      events: [
        {
          event: "click",
          target: "terminalOptions",
          handler: "handleTerminalChange",
        },
        {
          event: "click",
          target: "areaOptions",
          handler: "handleAreaChange",
        },
        {
          event: "click",
          target: "showCategoriesBtn",
          handler: "handleShowCategoriesBtnClick",
        },
        {
          event: "click",
          target: "hideCategoriesBtn",
          handler: "handleHideCategoriesBtnClick",
        },
        {
          event: "kl-in-vp",
          target: "this",
          handler: "onComponentInViewport",
        },
        {
          event: "kl-out-vp",
          target: "this",
          handler: "onComponentOutOfViewport",
        },
        {
          event: "scroll",
          target: "window",
          handler: "onScroll",
        },
      ],

      initialStates: FRALocationFilter.getInitialStateFromPageUrl(),

      reactions: {
        selectedTerminal: ["renderContent"],
        selectedHall: ["renderContent"],
        selectedArea: ["renderContent"],
        selectedCategory: ["renderContent"],
      },
    });
  }

  /**
   * Returns provided api-url from custom element
   * @returns {string}
   */
  get fetchUrl(): string {
    const { year: yyyy, month: mm, day: dd, hour: hh } = dateUtils.toParts(new Date());
    const suffix = `/${yyyy}${mm}${dd}${hh}/locations.json`;
    return `${this.baseUrl}${suffix}`;
  }

  /**
   * Lifecycle-Hook
   * @override
   */
  async afterComponentRender() {
    // to handle event kl-in-vp/kl-out-vp:
    this.viewportObserver.observe(this);

    await this.getData();
    this.renderContent();
  }

  async getData() {
    const data = await fetchJSON<LocationFilterApiResponse>(this.fetchUrl);
    const terminals = data.meta.terminals;
    this._terminals = terminals;
    this._halls = terminals.flatMap(terminal => terminal.halls).sort();
    this._metaInformation = data.meta;
    this._categories = data.meta.categories;
    this._locations = data.results;
  }

  renderContent() {
    // currently always a terminal is selected, but that might change
    const selectedTerminal = this._terminals.find(terminal => terminal.id === this.state.selectedTerminal);

    const halls = this._halls.map(hall => ({
      name: hall,
      selected: hall === this.state.selectedHall,
      disabled: Boolean(selectedTerminal && !selectedTerminal.halls.includes(hall)),
    }));

    const locations = this._locations.filter(location => {
      return (
        (!selectedTerminal || location.zone.terminal === selectedTerminal.id) &&
        (!this.state.selectedHall || location.zone.area === this.state.selectedHall) &&
        (!this.state.selectedArea || location.zone.landside === (this.state.selectedArea === "public")) &&
        (!this.state.selectedCategory || location.categories.includes(this.state.selectedCategory))
      );
    });

    // categories which combined with other filter will still have some results
    const possibleCategories = new Set(locations.flatMap(location => location.categories));

    const categories = this._categories
      .filter(category => possibleCategories.has(category.id))
      .map(category => ({ ...category, selected: category.id === this.state.selectedCategory }));

    // user has changed other options which make the selected category obsolete (would return no results), therefor the category selection is reset
    if (this.state.selectedCategory && !categories.some(category => category.selected)) {
      this.setState({ selectedCategory: "" });
      return;
    }

    this.renderTerminals();
    this.renderAreas();
    this.renderHalls(halls);
    this.renderCategories(categories);
    this.renderResults(locations);
    this.renderAlphabet(locations);
  }

  renderTerminals() {
    // markup is server side rendered, only update css classes
    (this.ui.terminalOptions as HTMLElement[]).forEach(el =>
      el.classList.toggle(ACTIVE, el.dataset.terminalId === this.state.selectedTerminal),
    );
  }

  renderAreas() {
    // markup is server side rendered, only update css classes
    (this.ui.areaOptions as HTMLElement[]).forEach(el =>
      el.classList.toggle(ACTIVE, el.dataset.areaId === this.state.selectedArea),
    );
  }

  /**
   * Renders results using lit
   * @param {Array<ResultInformation>} results
   */
  renderResults(results: Array<ResultInformation>): void {
    const template = locationFilterResultsTemplate(results, this._metaInformation);
    render(template, this.ui.resultContainer);
  }

  /**
   * Renders results using lit
   * @param {Array<Object>} halls
   */
  renderHalls(halls: Array<{ name: string; selected: boolean; disabled: boolean }>): void {
    const hallsTemplate = locationFilterHallsTemplate(halls, this.setState.bind(this));
    render(hallsTemplate, this.ui.hallsContainer);
  }

  /**
   * Renders results using lit
   * @param {Array<CategoryInformation>} categories
   */
  renderCategories(categories: Array<CategoryInformation & { selected: boolean }>): void {
    toggleClass(this.ui.categoryFilterContainer, SELECTED, Boolean(this.state.selectedCategory));

    const categoriesTemplate = locationFilterCategoriesTemplate(categories, this.setState.bind(this));
    render(categoriesTemplate, this.ui.categoryFilterContainer);
  }

  /**
   * Renders alphabet list using lit
   * @param {Array<ResultInformation>} results
   */
  renderAlphabet(results: Array<ResultInformation>): void {
    const alphabetTemplate = locationFilterAlphabetTemplate(results, this);
    render(alphabetTemplate, this.ui.alphabetContainer);
  }

  /**
   * handles change of terminal options
   * @param {Event} e
   */
  handleTerminalChange(e: Event): void {
    const selectedTerminal = (e.target as HTMLAnchorElement).dataset.terminalId;
    window.dataLayer?.push({
      event: "gaEvent",
      eventCategory: "locationFilter",
      eventAction: "Terminal switch",
      eventLabel: `Terminal ${selectedTerminal}`,
    });
    this.setState({ selectedTerminal, selectedHall: "" });
  }

  /**
   * handles change of area options
   * @param {Event} e
   */
  handleAreaChange(e: Event): void {
    const selectedArea = (e.target as HTMLAnchorElement).dataset.areaId;
    window.dataLayer?.push({
      event: "gaEvent",
      eventCategory: "locationFilter",
      eventAction: "Area switch",
      eventLabel: `Area ${selectedArea}`,
    });
    this.setState({ selectedArea: selectedArea === this.state.selectedArea ? "" : selectedArea });
  }

  /**
   * handles click on category-filters
   * @param {Event} e
   */
  handleShowCategoriesBtnClick(e: Event): void {
    e.preventDefault();
    addClass(this.ui.categoryFilterContainer, ACTIVE);
    addClass(this.ui.showCategoriesBtn, HIDDEN);
    removeClass(this.ui.hideCategoriesBtn, HIDDEN);
  }

  /**
   * handles click on category-filters
   * @param {Event} e
   */
  handleHideCategoriesBtnClick(e: Event): void {
    e.preventDefault();
    removeClass(this.ui.categoryFilterContainer, ACTIVE);
    removeClass(this.ui.showCategoriesBtn, HIDDEN);
    addClass(this.ui.hideCategoriesBtn, HIDDEN);
  }

  /**
   * handles click on alphabet anchors and scrolls page to target
   * @param {Event} e
   */
  handleAlphabetAnchorsClick(e: Event): void {
    e.preventDefault();
    const anchor = (e.target as HTMLAnchorElement).closest("a");
    if (anchor === null) return;
    const target = anchor.getAttribute("href") || "";
    if (target === null) return;

    const scrollTarget = this.ui.resultContainer.querySelector(target);
    this.detectScrollTargetPosition(scrollTarget);

    scrollTarget.scrollIntoView({
      behavior: "smooth",
    });

    const alphabetLinks = findAll(this, ".fra-m-location-filter__alphabet-link");
    alphabetLinks.forEach(el => {
      removeClass(el as HTMLAnchorElement, ACTIVE);
    });
    addClass(anchor, ACTIVE);
  }

  /**
   * checks if scroll-direction is "up" and dispatches event for fra-m-sticky-page-header-container
   * @param {HTMLElement} scrollTarget
   */
  detectScrollTargetPosition(scrollTarget: HTMLElement): void {
    const scrollValue = 0;
    const scrollTargetRect = scrollTarget.getBoundingClientRect();
    const scrollTargetPosition = scrollTargetRect.top;
    if (scrollValue - scrollTargetPosition > 0) {
      this.dispatchEvent(new CustomEvent(FRALocationFilter.AUTO_SCROLL, { bubbles: true }));
    }
  }

  /**
   * handles if component is in viewport
   */
  onComponentInViewport(): void {
    this._inViewport = true;
  }

  /**
   * handles if component is out of viewport
   */
  onComponentOutOfViewport(): void {
    this._inViewport = false;
  }

  /**
   * handles scrolling (and showing alphabet in MQ1-3) if component is in viewport
   */
  onScroll(): void {
    if (this._inViewport === true) {
      const componentPosition = this.getBoundingClientRect();
      const alphabetContainerPosition = this.ui.alphabetContainer.getBoundingClientRect();
      const filterContainerPosition = this.ui.filterContainer.getBoundingClientRect();

      const isOverlappingWithFilterContainer = filterContainerPosition.bottom > alphabetContainerPosition.top;
      const arrivedComponentBottom = componentPosition.bottom < alphabetContainerPosition.bottom;

      toggleClass(this.ui.alphabetContainer, HIDDEN, isOverlappingWithFilterContainer || arrivedComponentBottom);

      this.calculateIntersectionPosition();
    }
  }

  /**
   * calculates MQ-based top/bottom-Value of alphabetContainer to handle "collision" of resultsContainer
   */
  calculateIntersectionPosition(): void {
    const alphabetContainerPosition = this.ui.alphabetContainer.getBoundingClientRect();
    let collisionPosition = alphabetContainerPosition.top;
    // MQ1-2: check for intersecting top of alphabetContainer
    // MQ3-5: check for intersecting bottom of alphabetContainer
    if (getCurrentMQ() !== "MQ1" && getCurrentMQ() !== "MQ2") {
      collisionPosition = alphabetContainerPosition.top + this.ui.alphabetContainer.clientHeight;
    }
    this.handleResultsCollision(collisionPosition);
  }

  /**
   * handles "collision" of sections and alphabetContainer
   * @param {number} collisionPosition
   */
  handleResultsCollision(collisionPosition: number): void {
    const sectionIdentifiers = findAll(this, ".fra-m-location-filter__result-section-identifier");
    const alphabetLinks = findAll(this, ".fra-m-location-filter__alphabet-link");

    sectionIdentifiers.forEach((sectionIdentifier: any) => {
      const sectionIdentifierPosition = sectionIdentifier.getBoundingClientRect();
      const parentSectionContainer = getParent(
        sectionIdentifier,
        ".fra-m-location-filter__result-section",
      ) as HTMLDivElement;

      const topPosition = sectionIdentifierPosition.top - 30; // include space to top-border
      const containerBottomPosition = topPosition + parentSectionContainer.clientHeight;

      if (containerBottomPosition > collisionPosition && topPosition < collisionPosition) {
        const targetId = sectionIdentifier.getAttribute("id");
        alphabetLinks.forEach(alphabetLink => {
          removeClass(alphabetLink as HTMLAnchorElement, ACTIVE);
          const href = (alphabetLink as HTMLAnchorElement).getAttribute("href")?.substring(1) || "";
          if (href === targetId) {
            addClass(alphabetLink as HTMLAnchorElement, ACTIVE);
          }
        });
      }
    });
  }
}

customElements.define("fra-m-location-filter", FRALocationFilter);

export default FRALocationFilter;
